import { Injectable, inject } from '@angular/core';
import { Observable, defer, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpService } from '../core/http.service';

@Injectable({ providedIn: 'root' })
export class VehicleStorageService {
  private readonly httpService = inject(HttpService);

  private readonly VERSION_KEY = 'vehiclesStorageVersion';
  private readonly DATA_KEY = 'vehiclesStorageData';

  /**
   * If version matches cache -> return cached data
   * else -> fetch, store, return fetched data
   * If fetch fails -> fallback to cache if available, otherwise error
   */
  getVehicles<T = any>(version: string): Observable<T> {
    return defer(() => {
      const cachedVersion = this.readVersion();

      if (cachedVersion === version) {
        const cached = this.readData<T>();
        return cached ? of(cached) : throwError(() => new Error('Cached data is missing or corrupted'));
      }

      return (this.httpService.getAllVehicles() as Observable<T>).pipe(
        tap((vehicleData) => this.storeVehiclesData(version, vehicleData)),
        catchError((err) => {
          const cached = this.readData<T>();
          return cached ? of(cached) : throwError(() => err);
        })
      );
    });
  }

  private storeVehiclesData<T>(version: string, vehiclesData: T): void {
    try {
      localStorage.setItem(this.DATA_KEY, JSON.stringify(vehiclesData));
      localStorage.setItem(this.VERSION_KEY, version);
    } catch {
      // ignore storage errors
    }
  }

  private readVersion(): string | null {
    try {
      return localStorage.getItem(this.VERSION_KEY);
    } catch {
      return null;
    }
  }

  private readData<T>(): T | null {
    try {
      const raw = localStorage.getItem(this.DATA_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
}
